import React from 'react';
import { useField } from 'formik';
import { clsx } from 'clsx';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  name: string;
  label?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
  error?: string;
}

export function Input({
  name,
  label,
  helperText,
  leftIcon,
  rightIcon,
  containerClassName,
  className,
  required,
  disabled,
  error: externalError,
  ...props
}: InputProps) {
  // Try to use Formik if available, otherwise use controlled props
  let field: {
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent) => void;
    onBlur: (e: React.FocusEvent) => void;
  } | null = null;
  let meta: { touched?: boolean; error?: string } | null = null;
  let isFormikMode = false;

  try {
    const formikField = useField(name);
    field = formikField[0];
    meta = formikField[1];
    isFormikMode = true;
  } catch (error) {
    // Not in Formik context, use controlled mode
    isFormikMode = false;
  }

  const hasError = isFormikMode ? meta?.touched && meta?.error : externalError;

  return (
    <div className={clsx('w-full', containerClassName)}>
      {label && (
        <label
          htmlFor={name}
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="h-5 w-5 text-gray-400 dark:text-gray-500">{leftIcon}</span>
          </div>
        )}

        <input
          id={name}
          name={name}
          {...(isFormikMode ? field : {})}
          {...props}
          disabled={disabled}
          className={clsx(
            'block w-full rounded-lg border transition-colors duration-200',
            'px-4 py-2.5 text-base',
            'text-gray-900 dark:text-white',
            'placeholder-gray-400 dark:placeholder-gray-500',
            'bg-white dark:bg-dark-800',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            // Date input calendar icon dark mode support
            '[color-scheme:light] dark:[color-scheme:dark]',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            hasError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500 dark:border-dark-600',
            disabled && 'cursor-not-allowed bg-gray-50 opacity-50 dark:bg-dark-900',
            className
          )}
        />

        {rightIcon && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className="h-5 w-5 text-gray-400 dark:text-gray-500">{rightIcon}</span>
          </div>
        )}
      </div>

      {hasError && (
        <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
          {isFormikMode ? meta?.error : externalError}
        </p>
      )}

      {helperText && !hasError && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
